/**
 * The SmartLink analytics contract, shared by musicapi (which answers it),
 * server (which proxies it) and the renderer (which draws it).
 */

export interface SmartLinkStatsTotals {
  views: number;
  visitors: number;
  clicks: number;
  presaves: number;
  /** Share of views that led to a click, 0-100. Null when there are no views. */
  clickThroughRate: number | null;
  /** Plays of the Media blocks (`media_play`). Optional for old servers. */
  mediaPlays?: number;
}

export interface SmartLinkStatsSeriesPoint {
  /** YYYY-MM-DD */
  date: string;
  views: number;
  clicks: number;
  /** Plays of the Media blocks on that day. Optional for old servers. */
  mediaPlays?: number;
}

export interface SmartLinkStatsBreakdownItem {
  key: string;
  clicks: number;
  /** Share of the breakdown's total, 0-100. */
  percentage: number;
}

export interface SmartLinkStats {
  totals: SmartLinkStatsTotals;
  series: SmartLinkStatsSeriesPoint[];
  /** "Where they listened" — clicks per music service. */
  services: SmartLinkStatsBreakdownItem[];
  /** "Markets" — events per country. */
  countries: SmartLinkStatsBreakdownItem[];
  /** Per-block attribution, keyed by block uuid. */
  blocks: SmartLinkStatsBlockItem[];
  /**
   * One item per Media block that has a number in the window, also a block that was removed since.
   * Optional for old servers.
   */
  media?: SmartLinkStatsMediaItem[];
}

/** One day of one Media block. */
export interface SmartLinkStatsMediaSeriesPoint {
  /** YYYY-MM-DD */
  date: string;
  seen: number;
  plays: number;
}

/**
 * The numbers of one Media block in the window (design c1). Counted from the events of the public
 * page: `media_view` (seen), `media_play`, `media_unmute`, `media_progress` and the clicks of a
 * block that opens a link.
 */
export interface SmartLinkStatsMediaItem {
  blockUUID: string;
  /** `media_view`: half of the block was on the screen for 1 second, once per page view. */
  seen: number;
  /** A tap on play, or a muted autoplay that ran 3 seconds. */
  plays: number;
  /** The plays that started as a muted autoplay. */
  autoplayPlays: number;
  /** The fan turned the sound on. */
  unmutes: number;
  /** The plays that reached 25, 50, 75 and 100 % of the length. */
  quartiles: [number, number, number, number];
  /** The average share of the length that a play reached, 0-100; null with no play (`averageWatchedPct`). */
  avgWatchedPct: number | null;
  /** The clicks of a block that opens a link. */
  clicks: number;
  /** The unique visitors that saw the block. */
  visitors: number;
  series: SmartLinkStatsMediaSeriesPoint[];
}

/**
 * The average watched share of the plays, 0-100, from the quartile counts. A play counts at the
 * furthest quartile it reached (0 below 25 %), so the number is a floor, never more than the truth.
 * Null with no play. The counts are made monotone first: a page that lost an event between two
 * quartiles must not give a negative step.
 */
export function averageWatchedPct(plays: number, quartiles: readonly [number, number, number, number]): number | null {
  if (plays <= 0) {
    return null;
  }
  const reached: number[] = [];
  let ceiling = plays;
  for (const count of quartiles) {
    ceiling = Math.min(ceiling, Math.max(0, count));
    reached.push(ceiling);
  }
  const [q25, q50, q75, q100] = reached as [number, number, number, number];
  const watched = 25 * (q25 - q50) + 50 * (q50 - q75) + 75 * (q75 - q100) + 100 * q100;
  return Math.round((watched / plays) * 10) / 10;
}

export interface SmartLinkStatsBlockItem extends SmartLinkStatsBreakdownItem {
  blockType: string;
}

/**
 * The plan fence of a stats window. The server cuts the asked days to the history of the plan
 * (`historyDaysLimit`: Free 7, Artist 90, Pro and up 365). Absent on an old server.
 */
export interface SmartLinkStatsWindow {
  /** The days the server served: the asked days cut to the plan. */
  days?: number;
  /** True when the asked days were more than the plan shows. */
  limited?: boolean;
  /** The history the plan shows, in days. */
  historyDaysLimit?: number;
}

/** The windows a client may ask for with `?days=`. */
export const SMART_LINK_STATS_DAYS_OPTIONS = [7, 14, 28, 90, 180, 365] as const;

export interface SmartLinkStatsResponse extends SmartLinkStatsWindow {
  stats: SmartLinkStats;
  /**
   * The totals of the `days` days before the window. Only `GET /accounts/:id/stats` sends it.
   * Null when the days before the window are outside the plan history (2 × days > the limit).
   */
  previousTotals?: SmartLinkStatsTotals | null;
}

/** Per-SmartLink figures for the cards on the Smartlinks index. */
export interface SmartLinkStatsSummary {
  smartLinkId: number;
  clicks: number;
  /**
   * The clicks of the same number of days before the window. Absent on old servers and when the
   * days before the window are outside the plan history.
   */
  previousClicks?: number;
  /** One click count per day, oldest first, for the card's sparkline. */
  trend: number[];
}

export interface SmartLinkStatsSummariesResponse extends SmartLinkStatsWindow {
  summaries: SmartLinkStatsSummary[];
}
